/**
 * The signed-in session's account actions: log out, and switch to a linked account.
 * One place for both, so the header, the simple rail and any future surface behave the same.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/index.js';
import { listLinkedAccountsOptions, switchAccountMutation } from '../generated/api/@tanstack/react-query.gen.js';
import { authApi, type LinkedAccount, type UserStatus } from '../services/api/auth.api.js';
import { adaptMutation } from './adapt-mutation.js';
import { clearAuth } from '../services/api/session.js';

export function useLogout(): () => void {
  const navigate = useNavigate();
  return () => {
    // Best-effort server-side revocation of the cookie session; this tab and every other open tab sign out
    // regardless.
    authApi.logout().catch(() => {});
    clearAuth();
    navigate('/login');
  };
}

/** The accounts this person may switch to, and whether loading them failed (so the menu can say so). */
export function useLinkedAccounts(): { accounts: LinkedAccount[]; failed: boolean } {
  const userId = useAuthStore((s) => s.user?.id);
  const query = useQuery({ ...listLinkedAccountsOptions(), enabled: userId !== undefined });
  return { accounts: query.data ?? [], failed: query.isError };
}

export function useSwitchAccount() {
  const login = useAuthStore((s) => s.login);
  return useMutation({
    ...adaptMutation(switchAccountMutation(), (targetUserId: number) => ({ body: { targetUserId } })),
    onSuccess: (result) => {
      login(result.token ?? '', {
        id: String(result.user.id),
        email: result.user.email,
        username: result.user.username ?? undefined,
        fullName: result.user.fullName ?? undefined,
        status: result.user.status as UserStatus,
        organizationId: result.user.organizationId ?? 0,
        organizationName: result.user.organizationName ?? null,
        organizationPlan: result.user.organizationPlan ?? null,
        permissions: result.user.permissions,
      });
      // Reload so every cached view refetches under the new organization.
      window.location.reload();
    },
  });
}
